import React, { ReactElement, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Table as MUITable,
  Skeleton,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import moment from "moment";

import TablePagination from "../paginations/table-pagination";
import { NoData } from "..";
import { getValue } from "./helpers";
import { ROW_HEIGHT, table_sort } from "@/constants";

const DynamicTable = <T extends IBase>({
  data,
  onRowClick,
  columns,
  isLoading,
  handlePageChange,
  handleLimitChange,
  limit,
  page,
  total,
  hidePagination,
  TotalEstimatedRow,
  stickyHeader,
  renderFooter,
  hideScrollBars,
  isFullHeight,
  square,
  borderLess,
  containerStyles,
  contentIdKey,
  ...rest
}: DynamicTableProps<T>): ReactElement => {
  const extendedColumns = columns?.map((column) => {
    return { ...column, key: column.key.toString().split(".") };
  });

  const generateCell = <T extends IBase>(
    column: {
      key: string[];
      name: string;
      component?: (data: T, index: number) => React.ReactNode;
      hideEdit?: boolean;
      hideDelete?: boolean;
      align: "left" | "center" | "right";
      sorting?: boolean;
      infoTooltip?: string;
    },
    item: T,
    index: number
  ): React.ReactNode | string => {
    const cell = column.component
      ? column.component(item, index)
      : getValue(item, column.key);

    // Format Date objects to string before rendering
    if (cell instanceof Date) {
      return moment(cell).format("DD-MM-YYYY");
    }

    return cell;
  };

  const handleRequestSort = (property: string) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>("");

  const sortedData = useMemo(() => {
    if (!orderBy) return data;

    return data.slice().sort((a, b) => {
      const aValue = getValue(a, orderBy.split("."));
      const bValue = getValue(b, orderBy.split("."));

      if (aValue instanceof Date && bValue instanceof Date) {
        if (aValue < bValue) return order === "asc" ? -1 : 1;
        if (aValue > bValue) return order === "asc" ? 1 : -1;
        return 0;
      }

      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, orderBy, order]);

  if (data?.length === 0 && !isLoading) {
    return <NoData />;
  }

  const renderSkeletonCells = (rowIndex: number) => {
    return columns.map((column, colIndex) => (
      <TableCell
        key={`skeleton-cell-${rowIndex}-${colIndex}`}
        sx={{
          height: ROW_HEIGHT,
          width: column.width || "auto",
        }}
      >
        <Skeleton variant="rounded" height="100%" width="100%" />
      </TableCell>
    ));
  };
  const renderSkeletonRows = () => {
    return Array.from({ length: limit }, (_, rowIndex) => (
      <TableRow key={`skeleton-row-${rowIndex}`} sx={{ height: "48px" }}>
        {renderSkeletonCells(rowIndex)}
      </TableRow>
    ));
  };

  return (
    <Stack
      sx={{
        ...(isFullHeight && { height: "100%", flex: 1 }),
        ...(!square && { borderRadius: "8px" }),
        ...(!borderLess && { border: "1px solid #e6e6e9" }),
        ...(containerStyles && { ...containerStyles }),
      }}
    >
      <TableContainer
        {...(() => {
          const { fillerRowCount, ...restProps } = rest as any;
          return restProps;
        })()}
        className="table"
        sx={{
          ...(hideScrollBars && {
            "&::-webkit-scrollbar": {
              width: 0,
              height: 0,
            },
            ...(square && { borderRadius: 0 }),
          }),
            ...((rest as any).sx as any),
          // Ensure container allows footer to stick to bottom without extra gap
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          paddingBottom: 0,
        }}
      >
        <MUITable sx={{ minWidth: 650, height: "100%" }} stickyHeader={stickyHeader}>
          <TableHead>
            <TableRow>
              {extendedColumns?.map(({ name, key, align, sorting, infoTooltip, width, minWidth }) => (
                <TableCell
                  key={`table-header-${name}-${key}`}
                  align={key[0] === "action" ? "right" : align}
                  sx={{
                    width: width || "auto",
                    minWidth: minWidth || "auto",
                    padding: "8px 12px",
                    fontSize: "14px",
                    fontWeight: 400,
                    lineHeight: "20px",
                    backgroundColor: "#F7F7F7",
                    color: "#5C5C5C",
                    borderBottom: "none",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={key[0] === "action" ? "right" : align}
                    gap={0.3}
                    flexWrap="wrap"
                  >
                    <Box 
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "20px",
                        color: "inherit",
                        wordBreak: "break-word",
                      }}
                    >
                      {name}
                    </Box>
                    {infoTooltip && (
                      <Tooltip title={infoTooltip} placement="top">
                        <InfoIcon
                          sx={{
                            color: "#8E9494",
                            fontSize: 16,
                          }}
                        />
                      </Tooltip>
                    )}
                    {sorting && (
                      <IconButton
                        size="small"
                        onClick={() => handleRequestSort(key.join("."))}
                      >
                        <img src={table_sort} alt="sort icon" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? renderSkeletonRows()
              : sortedData?.map((item, index) => (
                  <TableRow
                    key={`table-content-${(() => {
                      const byContentKey = contentIdKey && (item as any)[contentIdKey];
                      if (byContentKey != null && byContentKey !== "") return String(byContentKey);
                      const byId = (item as any)._id ?? (item as any).id;
                      if (byId != null && byId !== "") return String(byId);
                      return `idx-${index}`;
                    })()}`}
                    sx={{
                      cursor: "pointer",
                      height: "48px",
                      backgroundColor: "#FFFFFF",
                    }}
                    onClick={() => {
                      onRowClick?.(item);
                    }}
                  >
                    {extendedColumns?.map((column) => (
                      <TableCell
                        key={column.key.join(".")}
                        sx={{
                          borderBottom:
                            sortedData?.length - 1 === index
                              ? "none"
                              : "1px solid #EBEBEB",
                          width: column?.width ?? "auto",
                          height: "48px",
                          backgroundColor: "#FFFFFF",
                          color: "#171717",
                          fontSize: "14px",
                          fontWeight: 400,
                          lineHeight: "20px",
                          padding: "12px 20px 12px 12px",
                        }}
                        align={
                          column.key[0] === "action" ? "right" : column.align
                        }
                      >
                        {generateCell(column, item, index)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            {/* Body filler rows to push footer while matching body row background */}
            {!isLoading && (rest as any)?.fillerRowCount
              ? Array.from({ length: Math.max(0, (rest as any).fillerRowCount as number) }).map((_, i) => (
                  <TableRow key={`body-filler-${i}`}>
                    <TableCell colSpan={columns.length} sx={{ height: 48, border: 0 }} />
                  </TableRow>
                ))
              : null}
            {TotalEstimatedRow && (
              <TableRow sx={{ backgroundColor: "#F0F1FF" }}>
                <TableCell colSpan={columns.length}>
                  {TotalEstimatedRow}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {renderFooter?.()}
        </MUITable>
      </TableContainer>
      {!hidePagination && (
        <Box borderTop={"1px solid #e6e6e9"}>
          <TablePagination
            handlePageChange={handlePageChange}
            handleLimitChange={handleLimitChange}
            limit={limit}
            page={page}
            total={total}
          />
        </Box>
      )}
    </Stack>
  );
};

export { DynamicTable };
export { default as VendorsTable } from "./vendors-table";
